import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyInsert = any;

// Asylum catalog ID
const ASYLUM_CATALOG_ID = 'e35eebb8-d41c-4250-a4e2-ebcd0605bc9d';

// Column mapping interface - maps system fields to CSV column names
export interface ColumnMapping {
  id?: string;
  nome?: string;
  telefone?: string;
  email?: string;
  etapa?: string;
  googleDrive?: string;
  biometria?: string;
  enderecoBiometria?: string;
  envioI589?: string;
  envioI765?: string;
  clock150Days?: string;
  fillingReceived?: string;
  eadSsn?: string;
  nota1?: string;
  nota2?: string;
  nota3?: string;
  nota4?: string;
  nota5?: string;
  // New fields for dependents and additional data
  birthDate?: string;
  aprovacaoDocumentacao?: string;
  aplicante?: string;
  dependente1?: string;
  dependente2?: string;
  dependente3?: string;
  biometriaDependente1?: string;
  biometriaDependente2?: string;
  biometriaDependente3?: string;
  closingDate?: string;
}

// Generic row type - just a record of strings
type CsvRow = Record<string, string>;

interface ImportRun {
  id: string;
  filename: string;
  pipeline: string;
  total_rows: number;
  processed_rows: number;
  success_count: number;
  error_count: number;
  skipped_count: number;
  status: string;
}

interface ImportRowResult {
  rowNumber: number;
  kommoLeadId: string;
  status: 'success' | 'error' | 'skipped';
  message: string;
  contactId?: string;
  dealId?: string;
  caseId?: string;
}

// Helper function to get value from row using mapping
const getRowValue = (row: CsvRow, mapping: ColumnMapping, field: keyof ColumnMapping): string | undefined => {
  const csvColumn = mapping[field];
  if (!csvColumn) return undefined;
  return row[csvColumn];
};

export function useKommoImport() {
  const { user } = useAuth();
  const [appUserId, setAppUserId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentRun, setCurrentRun] = useState<ImportRun | null>(null);
  const [results, setResults] = useState<ImportRowResult[]>([]);
  const [progress, setProgress] = useState(0);

  // Get app user id from auth user
  useEffect(() => {
    if (user?.id) {
      supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single()
        .then(({ data }) => {
          if (data) setAppUserId(data.id);
        });
    }
  }, [user?.id]);

  // Normalize phone number - remove special chars, keep + if present
  const normalizePhone = useCallback((phone: string | undefined): string | null => {
    if (!phone || phone.trim() === '') return null;
    
    // Get first phone if multiple (separated by comma)
    const firstPhone = phone.split(',')[0].trim();
    if (!firstPhone) return null;
    
    // Remove all special chars except + and digits
    let normalized = firstPhone.replace(/[^\d+]/g, '');
    
    // Remove leading apostrophe that Excel sometimes adds
    if (normalized.startsWith("'")) {
      normalized = normalized.substring(1);
    }
    
    // Must have at least 8 digits to be valid
    const digitsOnly = normalized.replace(/\D/g, '');
    if (digitsOnly.length < 8) return null;
    
    return normalized || null;
  }, []);

  // Normalize email - lowercase, trim, validate
  const normalizeEmail = useCallback((email: string | undefined): string | null => {
    if (!email || email.trim() === '') return null;
    
    // Get first email if multiple (separated by comma)
    const firstEmail = email.split(',')[0].trim().toLowerCase();
    if (!firstEmail) return null;
    
    // Skip invalid/placeholder emails
    const invalidEmails = ['x', 'info', 'n/a', 'na', '-', '.', '..'];
    if (invalidEmails.includes(firstEmail)) return null;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(firstEmail)) return null;
    
    return firstEmail;
  }, []);

  // Parse Kommo date format (dd.mm.yyyy or dd.mm.yyyy hh:mm:ss)
  const parseKommoDate = useCallback((dateStr: string | undefined): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    
    // Try dd.mm.yyyy format
    const match = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (match) {
      const [, day, month, year] = match;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    }
    
    return null;
  }, []);

  // Parse name into first and last name
  const parseName = useCallback((name: string | undefined): { firstName: string; lastName: string } => {
    if (!name || name.trim() === '') {
      return { firstName: 'Unknown', lastName: '' };
    }
    
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ')
    };
  }, []);

  // Check if row was already imported
  const checkIfAlreadyImported = useCallback(async (kommoLeadId: string): Promise<boolean> => {
    const { data } = await supabase
      .from('kommo_import_rows')
      .select('id')
      .eq('kommo_lead_id', kommoLeadId)
      .eq('status', 'success')
      .maybeSingle();
    
    return !!data;
  }, []);

  // Find contact by phone
  const findContactByPhone = useCallback(async (phone: string): Promise<{ id: string } | null> => {
    // Search by primary_phone containing the digits
    const digitsOnly = phone.replace(/\D/g, '');
    
    const { data } = await supabase
      .from('contacts')
      .select('id')
      .or(`primary_phone.ilike.%${digitsOnly}%`)
      .limit(1)
      .maybeSingle();
    
    return data;
  }, []);

  // Find deal by contact
  const findDealByContact = useCallback(async (contactId: string): Promise<{ id: string } | null> => {
    const { data } = await supabase
      .from('deals')
      .select('id')
      .eq('contact_id', contactId)
      .limit(1)
      .maybeSingle();
    
    return data;
  }, []);

  // Create contact
  const createContact = useCallback(async (
    firstName: string,
    lastName: string,
    phone: string | null,
    email: string | null,
    birthDate: string | null = null
  ): Promise<{ id: string }> => {
    const insertData: Record<string, unknown> = {
      first_name: firstName,
      last_name: lastName,
      primary_phone: phone,
      primary_email: email,
      contact_type: 'active_customer',
      customer_status: 'in_process',
      created_by: appUserId
    };
    
    if (birthDate) {
      insertData.date_of_birth = birthDate;
    }
    
    const { data, error } = await supabase
      .from('contacts')
      .insert(insertData)
      .select('id')
      .single();
    
    if (error) throw error;
    return data;
  }, [appUserId]);

  // Get event type ID by code - moved up because it's used by createDependentWithRelationship
  const getEventTypeId = useCallback(async (code: string): Promise<string | null> => {
    const { data } = await supabase
      .from('event_types')
      .select('id')
      .eq('code', code)
      .maybeSingle();
    
    return data?.id || null;
  }, []);

  // Create dependent contact with relationship
  const createDependentWithRelationship = useCallback(async (
    caseId: string,
    mainContactId: string,
    dependentName: string,
    slotIndex: number,
    biometricsDate: string | null
  ): Promise<string | null> => {
    if (!dependentName || dependentName.trim() === '') return null;
    
    // Parse dependent name
    const { firstName, lastName } = parseName(dependentName);
    
    // Create contact for dependent (no phone/email)
    const { data: depContact, error: depError } = await supabase
      .from('contacts')
      .insert({
        first_name: firstName,
        last_name: lastName,
        contact_type: 'active_customer',
        created_by: appUserId
      })
      .select('id')
      .single();
    
    if (depError) throw depError;
    
    // Create case_contact with role='dependent' and slot_index
    await supabase
      .from('case_contacts')
      .insert({
        case_id: caseId,
        contact_id: depContact.id,
        role: 'dependent',
        is_primary: false,
        slot_index: slotIndex,
        slot_label: `Dependente ${slotIndex}`,
        created_by: appUserId
      });
    
    // Create family relationship between main contact and dependent
    await supabase
      .from('contact_relationships')
      .insert({
        from_contact_id: mainContactId,
        to_contact_id: depContact.id,
        relationship_kind: 'family',
        relationship_type: 'Dependente',
        metadata: { slot_index: slotIndex, imported_from: 'kommo' }
      });
    
    // If has biometrics date, create event
    if (biometricsDate) {
      const eventTypeId = await getEventTypeId('biometrics_scheduled');
      if (eventTypeId) {
        await supabase
          .from('case_events')
          .insert({
            case_id: caseId,
            event_type_id: eventTypeId,
            event_date: biometricsDate,
            title: `Biometria - ${dependentName}`,
            additional_data: { 
              dependent_contact_id: depContact.id,
              dependent_name: dependentName,
              slot_index: slotIndex
            },
            created_by: user?.id
          } as AnyInsert);
      }
    }
    
    return depContact.id;
  }, [appUserId, parseName, getEventTypeId, user?.id]);

  // Create import deal
  const createImportDeal = useCallback(async (contactId: string, contactName: string): Promise<{ id: string }> => {
    const { data, error } = await supabase
      .from('deals')
      .insert({
        contact_id: contactId,
        source: 'direct',
        stage: 'WON',
        name: `Import Kommo - ${contactName}`,
        notes: 'Importado do Kommo - Pipeline Asilo',
        created_by: appUserId
      } as AnyInsert)
      .select('id')
      .single();
    
    if (error) throw error;
    return data;
  }, [appUserId]);

  // Create item and case (with anti-duplicate check)
  const createItemAndCase = useCallback(async (
    dealId: string,
    googleDriveUrl: string | null,
    legacyStage: string | null,
    closingDate: string | null
  ): Promise<{ itemId: string; caseId: string }> => {
    // Determine case_status based on "Etapa do lead"
    // 'approved' if "Closed Won", 'preparation' for all others
    const caseStatus = legacyStage?.toLowerCase().includes('closed won') 
      ? 'approved' 
      : 'preparation';
    
    // Parse closingDate to ensure it's a valid ISO timestamp
    // closingDate comes as YYYY-MM-DD from parseKommoDate
    const createdAtTimestamp = closingDate 
      ? new Date(closingDate + 'T00:00:00Z').toISOString()
      : null;
    
    // ✅ ANTI-DUPLICATE CHECK: Verify if deal already has an item with the same catalog_id
    const { data: existingItems } = await supabase
      .from('items')
      .select('id')
      .eq('deal_id', dealId)
      .eq('catalog_id', ASYLUM_CATALOG_ID);
    
    if (existingItems && existingItems.length > 0) {
      const existingItemId = existingItems[0].id;
      
      // Check if this item already has a case
      const { data: existingCases } = await supabase
        .from('cases')
        .select('id')
        .eq('item_id', existingItemId);
      
      if (existingCases && existingCases.length > 0) {
        // Item + Case já existem → retorna os IDs existentes (não cria duplicado)
        return { itemId: existingItemId, caseId: existingCases[0].id };
      }
      
      // Item existe mas sem case → cria apenas o case
      const { data: newCase, error: caseError } = await supabase
        .from('cases')
        .insert({
          item_id: existingItemId,
          status: caseStatus,
          google_drive_url: googleDriveUrl,
          legacy_pipeline: 'Asilo',
          legacy_stage: legacyStage,
          ...(createdAtTimestamp && { created_at: createdAtTimestamp })
        })
        .select('id')
        .single();
      
      if (caseError) throw caseError;
      return { itemId: existingItemId, caseId: newCase.id };
    }
    
    // Não existe item → criar item + case normalmente
    const { data: item, error: itemError } = await supabase
      .from('items')
      .insert({
        deal_id: dealId,
        catalog_id: ASYLUM_CATALOG_ID,
        status: 'sold',
        quantity: 1,
        unit_price: 0
      })
      .select('id')
      .single();
    
    if (itemError) throw itemError;
    
    // Create case with optional closingDate as created_at (as full ISO timestamp)
    const { data: caseData, error: caseError } = await supabase
      .from('cases')
      .insert({
        item_id: item.id,
        status: caseStatus,
        google_drive_url: googleDriveUrl,
        legacy_pipeline: 'Asilo',
        legacy_stage: legacyStage,
        ...(createdAtTimestamp && { created_at: createdAtTimestamp })
      })
      .select('id')
      .single();
    
    if (caseError) throw caseError;
    
    return { itemId: item.id, caseId: caseData.id };
  }, []);

  // Create case contact relation
  const createCaseContact = useCallback(async (caseId: string, contactId: string): Promise<void> => {
    await supabase
      .from('case_contacts')
      .insert({
        case_id: caseId,
        contact_id: contactId,
        role: 'applicant',
        is_primary: true,
        created_by: appUserId
      });
  }, [appUserId]);

  // Create case events from row data (using mapping)
  const createCaseEvents = useCallback(async (
    caseId: string, 
    row: CsvRow, 
    mapping: ColumnMapping
  ): Promise<void> => {
    const events: { eventTypeCode: string; eventDate: string | null; additionalData?: Record<string, unknown>; title?: string }[] = [];
    
    // Biometrics scheduled (applicant)
    const biometricsDate = parseKommoDate(getRowValue(row, mapping, 'biometria'));
    if (biometricsDate) {
      events.push({
        eventTypeCode: 'biometrics_scheduled',
        eventDate: biometricsDate,
        title: 'Biometria - Aplicante',
        additionalData: { address: getRowValue(row, mapping, 'enderecoBiometria') || null, is_applicant: true }
      });
    }
    
    // Aprovação da Documentação (Documentation Approval)
    const aprovacaoDate = parseKommoDate(getRowValue(row, mapping, 'aprovacaoDocumentacao'));
    if (aprovacaoDate) {
      events.push({
        eventTypeCode: 'approval_notice',
        eventDate: aprovacaoDate,
        title: 'Aprovação da Documentação',
        additionalData: { type: 'documentation_approved' }
      });
    }
    
    // Filing I-589
    const i589Date = parseKommoDate(getRowValue(row, mapping, 'envioI589'));
    if (i589Date) {
      events.push({
        eventTypeCode: 'filing_submitted',
        eventDate: i589Date,
        additionalData: { form: 'I-589' }
      });
    }
    
    // Filing I-765
    const i765Date = parseKommoDate(getRowValue(row, mapping, 'envioI765'));
    if (i765Date) {
      events.push({
        eventTypeCode: 'filing_submitted',
        eventDate: i765Date,
        additionalData: { form: 'I-765' }
      });
    }
    
    // 150 Days Clock
    const clockDate = parseKommoDate(getRowValue(row, mapping, 'clock150Days'));
    if (clockDate) {
      events.push({
        eventTypeCode: 'clock_started',
        eventDate: clockDate
      });
      
      // Also update case with clock start
      await supabase
        .from('cases')
        .update({ clock_150_days_start: clockDate })
        .eq('id', caseId);
    }
    
    // I-797C Received
    const fillingDate = parseKommoDate(getRowValue(row, mapping, 'fillingReceived'));
    if (fillingDate) {
      events.push({
        eventTypeCode: 'i797c_received',
        eventDate: fillingDate
      });
    }
    
    // EAD/SSN Received
    const eadSsnDate = parseKommoDate(getRowValue(row, mapping, 'eadSsn'));
    if (eadSsnDate) {
      events.push({
        eventTypeCode: 'ead_received',
        eventDate: eadSsnDate,
        additionalData: { combined_with_ssn: true }
      });
    }
    
    // Insert all events
    for (const event of events) {
      const eventTypeId = await getEventTypeId(event.eventTypeCode);
      if (eventTypeId) {
        await supabase
          .from('case_events')
          .insert({
            case_id: caseId,
            event_type_id: eventTypeId,
            event_date: event.eventDate,
            title: event.title || null,
            additional_data: event.additionalData || {},
            created_by: user?.id
          } as AnyInsert);
      }
    }
  }, [parseKommoDate, getEventTypeId, user?.id]);

  // Process dependents from row data
  const processDependents = useCallback(async (
    caseId: string,
    mainContactId: string,
    row: CsvRow,
    mapping: ColumnMapping
  ): Promise<void> => {
    const dependentFields = [
      { nameField: 'dependente1' as const, bioField: 'biometriaDependente1' as const, slot: 1 },
      { nameField: 'dependente2' as const, bioField: 'biometriaDependente2' as const, slot: 2 },
      { nameField: 'dependente3' as const, bioField: 'biometriaDependente3' as const, slot: 3 },
    ];

    for (const dep of dependentFields) {
      const depName = getRowValue(row, mapping, dep.nameField);
      const depBio = parseKommoDate(getRowValue(row, mapping, dep.bioField));
      
      if (depName && depName.trim()) {
        await createDependentWithRelationship(
          caseId,
          mainContactId,
          depName,
          dep.slot,
          depBio
        );
      }
    }
  }, [parseKommoDate, createDependentWithRelationship]);

  // Import notes to notes table (using mapping)
  const importNotes = useCallback(async (
    caseId: string, 
    row: CsvRow, 
    mapping: ColumnMapping
  ): Promise<void> => {
    const noteFields: { mapKey: keyof ColumnMapping; title: string }[] = [
      { mapKey: 'nota1', title: 'Nota 1' },
      { mapKey: 'nota2', title: 'Nota 2' },
      { mapKey: 'nota3', title: 'Nota 3' },
      { mapKey: 'nota4', title: 'Nota 4' },
      { mapKey: 'nota5', title: 'Nota 5' },
    ];
    
    for (const field of noteFields) {
      const noteContent = getRowValue(row, mapping, field.mapKey);
      if (noteContent && noteContent.trim()) {
        await supabase
          .from('notes')
          .insert({
            case_id: caseId,
            title: field.title,
            content: noteContent.trim(),
            created_by: appUserId
          } as AnyInsert);
      }
    }
  }, [appUserId]);

  // Start a new import run
  const startImportRun = useCallback(async (filename: string, totalRows: number): Promise<string> => {
    const { data, error } = await supabase
      .from('kommo_import_runs')
      .insert({
        filename,
        pipeline: 'asilo',
        total_rows: totalRows,
        status: 'processing',
        created_by: appUserId
      })
      .select()
      .single();
    
    if (error) throw error;
    
    setCurrentRun(data);
    return data.id;
  }, [appUserId]);

  // Process a single row (using mapping)
  const processRow = useCallback(async (
    runId: string,
    rowNumber: number,
    row: CsvRow,
    mapping: ColumnMapping
  ): Promise<ImportRowResult> => {
    const kommoLeadId = getRowValue(row, mapping, 'id') || `row-${rowNumber}`;
    
    try {
      // Check if already imported
      const alreadyImported = await checkIfAlreadyImported(kommoLeadId);
      if (alreadyImported) {
        const result: ImportRowResult = {
          rowNumber,
          kommoLeadId,
          status: 'skipped',
          message: 'Já importado anteriormente'
        };
        
        await supabase
          .from('kommo_import_rows')
          .upsert({
            run_id: runId,
            row_number: rowNumber,
            kommo_lead_id: kommoLeadId,
            raw_data: row as unknown as Record<string, unknown>,
            status: 'skipped',
            message: result.message
          } as AnyInsert, { onConflict: 'kommo_lead_id' });
        
        return result;
      }
      
      // Parse data using mapping
      const { firstName, lastName } = parseName(getRowValue(row, mapping, 'nome'));
      const phone = normalizePhone(getRowValue(row, mapping, 'telefone'));
      const email = normalizeEmail(getRowValue(row, mapping, 'email'));
      const birthDate = parseKommoDate(getRowValue(row, mapping, 'birthDate'));
      const googleDriveUrl = getRowValue(row, mapping, 'googleDrive') || null;
      const legacyStage = getRowValue(row, mapping, 'etapa') || null;
      const closingDate = parseKommoDate(getRowValue(row, mapping, 'closingDate'));
      
      let contactId: string;
      let dealId: string;
      let isNewContact = false;
      let isNewDeal = false;
      
      // 1. Try to find contact by phone
      if (phone) {
        const existingContact = await findContactByPhone(phone);
        if (existingContact) {
          contactId = existingContact.id;
        } else {
          // Create new contact with birthDate
          const newContact = await createContact(firstName, lastName, phone, email, birthDate);
          contactId = newContact.id;
          isNewContact = true;
        }
      } else {
        // No phone - create new contact anyway with birthDate
        const newContact = await createContact(firstName, lastName, null, email, birthDate);
        contactId = newContact.id;
        isNewContact = true;
      }
      
      // 2. Check if contact has WON deal (reutilizar se existir)
      let usedExistingWonDeal = false;
      let originalDealName = '';
      let itemId: string;
      let caseId: string;
      
      const { data: wonDeals } = await supabase
        .from('deals')
        .select('id, name')
        .eq('contact_id', contactId)
        .eq('stage', 'won')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (wonDeals && wonDeals.length > 0) {
        // TEM Deal WON → NÃO cria novo, usa o existente
        dealId = wonDeals[0].id;
        originalDealName = wonDeals[0].name || '';
        usedExistingWonDeal = true;
      } else {
        // NÃO TEM Deal WON → Cria novo deal
        const newDeal = await createImportDeal(contactId, `${firstName} ${lastName}`.trim());
        dealId = newDeal.id;
        isNewDeal = true;
      }
      
      // 3. SEMPRE criar item e case (vinculado ao deal)
      const itemAndCase = await createItemAndCase(dealId, googleDriveUrl, legacyStage, closingDate);
      itemId = itemAndCase.itemId;
      caseId = itemAndCase.caseId;
      
      // 4. Create case contact relation
      await createCaseContact(caseId, contactId);
      
      // 5. Create case events
      await createCaseEvents(caseId, row, mapping);
      
      // 6. Process dependents (create contacts, case_contacts, relationships, biometrics events)
      await processDependents(caseId, contactId, row, mapping);
      
      // 7. Import notes
      await importNotes(caseId, row, mapping);
      
      // 8. Se usou Deal WON existente → Cria task de revisão com botões de ação
      let taskCreated = false;
      if (usedExistingWonDeal) {
        await supabase
          .from('tasks')
          .insert({
            case_id: caseId,
            title: '⚠️ Verificar: Case importado vinculado a Deal WON existente',
            description: `Este case foi importado do Kommo e vinculado ao Deal "${originalDealName}" que já estava fechado.\n\nVerifique se este processo pertence a esse deal. Se NÃO pertencer, clique em "Não Pertence" para criar um novo deal.`,
            status: 'pending',
            priority: 'high',
            created_by: appUserId,
            metadata: {
              type: 'kommo_review',
              case_id: caseId,
              item_id: itemId,
              original_deal_id: dealId,
              contact_id: contactId
            }
          });
        taskCreated = true;
      }
      
      // Build success message
      const parts: string[] = [];
      if (isNewContact) parts.push('Contato criado');
      else parts.push('Contato encontrado');
      if (usedExistingWonDeal) parts.push('Deal WON reutilizado');
      else if (isNewDeal) parts.push('Deal criado');
      parts.push('Case criado');
      if (taskCreated) parts.push('Task de revisão criada');
      
      const result: ImportRowResult = {
        rowNumber,
        kommoLeadId,
        status: 'success',
        message: parts.join(' | '),
        contactId,
        dealId,
        caseId
      };
      
      // Record success
      await supabase
        .from('kommo_import_rows')
        .upsert({
          run_id: runId,
          row_number: rowNumber,
          kommo_lead_id: kommoLeadId,
          raw_data: row as unknown as Record<string, unknown>,
          status: 'success',
          message: result.message,
          contact_id: contactId,
          deal_id: dealId,
          case_id: caseId
        } as AnyInsert, { onConflict: 'kommo_lead_id' });
      
      return result;
      
    } catch (error: any) {
      let errorMessage = 'Erro desconhecido';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.details) {
        errorMessage = error.details;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      const result: ImportRowResult = {
        rowNumber,
        kommoLeadId,
        status: 'error',
        message: errorMessage
      };
      
      // Record error
      await supabase
        .from('kommo_import_rows')
        .upsert({
          run_id: runId,
          row_number: rowNumber,
          kommo_lead_id: kommoLeadId,
          raw_data: row as unknown as Record<string, unknown>,
          status: 'error',
          message: errorMessage
        } as AnyInsert, { onConflict: 'kommo_lead_id' });
      
      return result;
    }
  }, [
    checkIfAlreadyImported,
    parseName,
    normalizePhone,
    normalizeEmail,
    parseKommoDate,
    findContactByPhone,
    findDealByContact,
    createContact,
    createImportDeal,
    createItemAndCase,
    createCaseContact,
    createCaseEvents,
    processDependents,
    importNotes
  ]);

  // Run full import (with mapping)
  const runImport = useCallback(async (rows: CsvRow[], mapping: ColumnMapping): Promise<void> => {
    setIsProcessing(true);
    setResults([]);
    setProgress(0);
    
    try {
      const runId = await startImportRun('kommo-import.csv', rows.length);
      
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const result = await processRow(runId, i + 1, row, mapping);
        
        setResults(prev => [...prev, result]);
        
        if (result.status === 'success') successCount++;
        else if (result.status === 'error') errorCount++;
        else if (result.status === 'skipped') skippedCount++;
        
        const newProgress = Math.round(((i + 1) / rows.length) * 100);
        setProgress(newProgress);
        
        // Update run progress
        await supabase
          .from('kommo_import_runs')
          .update({
            processed_rows: i + 1,
            success_count: successCount,
            error_count: errorCount,
            skipped_count: skippedCount
          })
          .eq('id', runId);
      }
      
      // Mark run as completed
      await supabase
        .from('kommo_import_runs')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', runId);
      
      setCurrentRun(prev => prev ? { ...prev, status: 'completed' } : null);
      
    } catch (error) {
      console.error('Import failed:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [startImportRun, processRow]);

  // Validate rows before import (with mapping)
  const validateRows = useCallback(async (
    rows: CsvRow[], 
    mapping: ColumnMapping
  ): Promise<{
    valid: CsvRow[];
    invalid: { row: CsvRow; reason: string }[];
    alreadyImported: CsvRow[];
  }> => {
    const valid: CsvRow[] = [];
    const invalid: { row: CsvRow; reason: string }[] = [];
    const alreadyImported: CsvRow[] = [];
    
    for (const row of rows) {
      const kommoLeadId = getRowValue(row, mapping, 'id');
      const nome = getRowValue(row, mapping, 'nome');
      
      // Check required fields - nome is required
      if (!nome || !nome.trim()) {
        invalid.push({ row, reason: 'Nome ausente' });
        continue;
      }
      
      // Check if already imported (only if we have an ID)
      if (kommoLeadId) {
        const imported = await checkIfAlreadyImported(kommoLeadId);
        if (imported) {
          alreadyImported.push(row);
          continue;
        }
      }
      
      valid.push(row);
    }
    
    return { valid, invalid, alreadyImported };
  }, [checkIfAlreadyImported]);

  return {
    isProcessing,
    currentRun,
    results,
    progress,
    runImport,
    validateRows,
    normalizePhone,
    normalizeEmail,
    parseKommoDate
  };
}
